import { Middleware } from '@reduxjs/toolkit';
import { t } from 'i18next';

import {
  confirmPaymentFailure,
  fetchCertainPaymentMethodFailure,
  fetchCertainPaymentMethodSuccess,
  fetchPaymentCurrentStateSuccess,
  fetchPaymentInfoSuccess,
  processPaymentFailure,
  registrationPaymentFailure,
  registrationPaymentSuccess,
  setActiveScreen,
  setPaymentResult,
} from '@cashier/cashier-data-access';
import {
  DynamicScreens,
  FinalStates,
  PaymentTypes,
  ResultMessageTypes,
} from '@cashier/cashier-model';
import { PaymentStateChecker } from '@cashier/cashier-shared';

export const paymentResultMiddleware: Middleware =
  (store) => (next) => (action) => {
    const result = next(action);

    const {
      cashierConfiguration: { activeType },
      paymentMethods: {
        paymentMethod: { final_screen_description },
      },
      paymentProcessing: {
        result: { resultAction: processingResultAction },
      },
    } = store.getState();

    const translatedPaymentTypeMap: { [key: string]: string } = {
      [PaymentTypes.Deposit]: t('Deposit'),
      [PaymentTypes.Withdrawal]: t('Withdrawal'),
    };

    if (
      [
        processPaymentFailure.toString(),
        confirmPaymentFailure.toString(),
      ].includes(action.type)
    ) {
      store.dispatch(
        setPaymentResult({
          finalState: FinalStates.ERROR,
          message: {
            content: action.payload.message,
            type: ResultMessageTypes.HTML,
          },
        }),
      );
    }

    if (
      [
        fetchPaymentCurrentStateSuccess.toString(),
        fetchPaymentInfoSuccess.toString(),
      ].includes(action.type)
    ) {
      const { state } = action.payload;

      if (PaymentStateChecker.isSuccess(activeType, state)) {
        store.dispatch(
          setPaymentResult({
            finalState: FinalStates.SUCCESS,
            message: {
              content: t('successfulTransactionComplex.message', {
                transactionType: translatedPaymentTypeMap[activeType],
              }),
              type: ResultMessageTypes.STRING,
            },
          }),
        );
      }

      if (PaymentStateChecker.isFailed(activeType, state)) {
        store.dispatch(
          setPaymentResult({
            finalState: FinalStates.ERROR,
            message: {
              content: t('failedTransaction.message'),
              type: ResultMessageTypes.STRING,
            },
          }),
        );
      }

      if (
        PaymentStateChecker.isPending(activeType, state) &&
        !processingResultAction
      ) {
        store.dispatch(
          setPaymentResult({
            finalState: FinalStates.PENDING,
            message: {
              content: final_screen_description,
              type: ResultMessageTypes.HTML,
            },
          }),
        );
      }
    }

    if (action.type === registrationPaymentSuccess.toString()) {
      store.dispatch(
        setPaymentResult({
          finalState: FinalStates.SUCCESS,
          message: {
            content: t('successfulRegistrationChannel.message'),
            type: ResultMessageTypes.STRING,
          },
        }),
      );
    }

    if (action.type === registrationPaymentFailure.toString()) {
      store.dispatch(
        setPaymentResult({
          finalState: FinalStates.ERROR,
          message: {
            content:
              action.payload.message || t('failureRegistrationChannel.message'),
            type: ResultMessageTypes.STRING,
          },
        }),
      );
    }

    if (action.type === fetchCertainPaymentMethodFailure.toString()) {
      store.dispatch(
        setPaymentResult({
          finalState: FinalStates.PAYMENT_METHOD_ERROR,
          message: {
            content: action.payload,
            type: ResultMessageTypes.STRING,
          },
        }),
      );
    }

    if (action.type === fetchCertainPaymentMethodSuccess.toString()) {
      const {
        paymentMethods: {
          paymentMethod: { restriction },
        },
      } = store.getState();

      if (restriction) {
        store.dispatch(
          setPaymentResult({
            finalState: FinalStates.RESTRICTED,
            message: {
              content: restriction.message,
              type: ResultMessageTypes.STRING,
            },
          }),
        );
        store.dispatch(setActiveScreen(DynamicScreens.FinalScreen));
      }
    }

    return result;
  };
